import PublicHeader from "./PublicHeader";
import React, { useEffect, useRef, useState } from "react";
import { signUp, confirmSignUp, resendSignUpCode, signIn, fetchAuthSession, signOut } from "@aws-amplify/auth";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { getSafeReaderReturnTo } from "../../auth/readerReturn";
import { displayLabel } from "../../utils/displayLabel";
import { genreColor } from "../../utils/genreColor";
import { configureAuth } from "../../auth/authConfig";
import { persistAuthType } from "../../auth/authTypeStorage";
import { Eye, EyeOff, Check } from "lucide-react";

const GENRES_API = "https://8apocg3o77.execute-api.us-west-2.amazonaws.com/default/get-available-genres";
const PENDING_KEY = "bookish-reader-signup";
const PROVISION_API = "https://0gjzxw5ijj.execute-api.us-west-2.amazonaws.com/default/newreaderuserS3trigger";
const inputClass = "w-full px-4 py-2 min-h-[44px] border border-bookish-purple/15 rounded-lg text-sm bg-white focus:border-bookish-purple/40 focus:ring-2 focus:ring-bookish-purple/10 focus:outline-none";
const buttonClass = "w-full bg-bookish-royal hover:bg-bookish-purpleAccent text-white py-2 min-h-[44px] rounded-lg font-semibold transition-colors disabled:opacity-60 disabled:cursor-wait";

function normalizeGenres(values) {
  const seen = new Set();
  return values.filter(value => typeof value === "string").map(value => {
    const clean = value.trim();
    return ["romantasy", "romantic fantasy", "fantasy romance"].includes(clean.toLowerCase()) ? "Romantasy" : clean;
  }).filter(value => {
    if (!value || seen.has(value.toLowerCase())) return false;
    seen.add(value.toLowerCase());
    return true;
  });
}

function genreLines(value) {
  const words = displayLabel(value).split(/\s+/);
  if (words.length < 2) return [words[0]];
  let split = 1;
  for (let i = 2; i < words.length; i++) {
    if (Math.abs(words.slice(0, i).join(" ").length - words.slice(i).join(" ").length) <
        Math.abs(words.slice(0, split).join(" ").length - words.slice(split).join(" ").length)) split = i;
  }
  return [words.slice(0, split).join(" "), words.slice(split).join(" ")];
}

function readPending() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(PENDING_KEY));
    if (!saved || typeof saved.email !== "string" || !saved.email) return null;
    return { email: saved.email, confirmed: saved.confirmed === true, returnTo: getSafeReaderReturnTo(saved.returnTo),
      firstName: typeof saved.firstName === "string" ? saved.firstName : "",
      lastName: typeof saved.lastName === "string" ? saved.lastName : "",
      genres: normalizeGenres(Array.isArray(saved.genres) ? saved.genres : []).slice(0, 3) };
  } catch { return null; }
}

function friendlyError(error) {
  const messages = {
    UsernameExistsException: "An account already uses this email. Log in, or choose ‘Finish verifying an existing account’ below.",
    InvalidPasswordException: "This password does not meet the account requirements. Try a longer password with uppercase and lowercase letters, a number, and a symbol.",
    CodeMismatchException: "That code doesn’t match. Check the latest email and try again.",
    ExpiredCodeException: "That code has expired. Request a new code below.",
    LimitExceededException: "Too many attempts. Please wait a little before trying again.",
    TooManyRequestsException: "Too many attempts. Please wait a little before trying again.",
    NotAuthorizedException: "We couldn’t sign you in. Check your password, or use the login page to reset it.",
    UserNotFoundException: "We couldn’t find that account. Check your email address or create an account.",
    UserNotConfirmedException: "Your email still needs verification. Choose ‘Enter a verification code’ below.",
    InvalidParameterException: "Please check your details and try again. If you already verified your email, choose ‘Continue setup’ below.",
  };
  return messages[error?.name] || "We couldn’t complete that step. Please try again. If it keeps happening, contact Lady Liza below.";
}

export default function SignUpReader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [pending] = useState(readPending);
  const requestedReturn = new URLSearchParams(location.search).get('returnTo');
  const returnTo = getSafeReaderReturnTo(requestedReturn === null ? pending?.returnTo : requestedReturn);
  const loginPath = returnTo ? `/loginreader?${new URLSearchParams({ returnTo })}` : '/loginreader';
  const [step, setStep] = useState(location.state?.resumeVerification ? 'resume' : pending ? (!pending.firstName.trim() || !pending.genres.length ? "resume" : pending.confirmed ? "setup" : "verify") : "signup");
  const [email, setEmail] = useState(location.state?.resumeVerification && typeof location.state.email === 'string' ? location.state.email : pending?.email || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [firstName, setFirstName] = useState(pending?.firstName || "");
  const [lastName, setLastName] = useState(pending?.lastName || "");
  const [genres, setGenres] = useState(pending?.genres || []);
  const [availableGenres, setAvailableGenres] = useState([]);
  const [genreStatus, setGenreStatus] = useState("loading");
  const [genreRetry, setGenreRetry] = useState(0);
  const [verificationCode, setVerificationCode] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const lock = useRef(false);
  const heading = useRef(null);

  useEffect(() => { configureAuth("reader"); }, []);
  useEffect(() => { if (step !== "signup") heading.current?.focus(); }, [step]);
  useEffect(() => {
    if (step !== "complete") return undefined;
    const timer = setTimeout(() => navigate(loginPath, { replace: true }), 900);
    return () => clearTimeout(timer);
  }, [step, navigate, loginPath]);
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const timeout = setTimeout(() => controller.abort(), 15000);
    setGenreStatus("loading");
    async function load() {
      try {
        const response = await fetch(GENRES_API, { signal: controller.signal });
        if (!response.ok) throw new Error("Genres unavailable");
        const data = await response.json();
        if (!Array.isArray(data.genres)) throw new Error("Invalid genre list");
        const options = normalizeGenres(data.genres);
        if (!options.length) throw new Error("Empty genre list");
        if (!options.includes("Romantasy")) {
          const fantasy = options.findIndex(genre => genre.toLowerCase() === "fantasy");
          options.splice(fantasy < 0 ? options.length : fantasy + 1, 0, "Romantasy");
        }
        if (active) { setAvailableGenres(options); setGenreStatus("ready"); }
      } catch {
        if (active) setGenreStatus("error");
      } finally { clearTimeout(timeout); }
    }
    load();
    return () => { active = false; controller.abort(); clearTimeout(timeout); };
  }, [genreRetry]);

  function remember(confirmed) {
    // Progress is only a UI hint. Authentication is still required before setup.
    // Never persist the password or verification code.
    try {
      sessionStorage.setItem(PENDING_KEY, JSON.stringify({
        email: email.trim(), confirmed, firstName: firstName.trim(), lastName: lastName.trim(), genres,
        ...(returnTo ? { returnTo } : {}),
      }));
    } catch { /* Signup also works when browser storage is unavailable. */ }
  }

  async function run(label, action) {
    if (lock.current) return;
    lock.current = true;
    setBusy(label);
    setError("");
    setMessage("");
    try {
      configureAuth("reader");
      await action();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      lock.current = false;
      setBusy("");
    }
  }

  async function finishSetup() {
    setBusy("Opening your account…");
    persistAuthType("reader");
    let result;
    try {
      result = await signIn({ username: email.trim(), password });
    } catch (err) {
      if (err.name !== "UserAlreadyAuthenticatedException") throw err;
      // Replace this browser's session without signing out other devices.
      await signOut();
      result = await signIn({ username: email.trim(), password });
    }
    if (!result.isSignedIn) {
      setError("Your email step is complete, but sign-in needs another step. Please continue through Reader Login below.");
      return;
    }
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (!idToken) {
      setError("We couldn’t open your session. Retry setup, or continue through Reader Login below.");
      return;
    }
    setBusy("Setting up your reader home…");
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch(PROVISION_API, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: idToken },
        body: JSON.stringify({ firstName: firstName.trim(), lastName: lastName.trim(), genres }),
        signal: controller.signal,
      });
      if (!response.ok) throw new Error("Provisioning failed");
    } catch {
      setError("Your email is verified, but we couldn’t finish setting up your reader home. Retry setup below. You don’t need to create another account or verify again.");
      return;
    } finally {
      clearTimeout(timeout);
    }
    try { sessionStorage.removeItem(PENDING_KEY); } catch { /* Storage may be disabled. */ }
    setMessage("Opening your Reader Portal…");
    // Login restores the shared application user state before entering the portal.
    setStep("complete");
  }

  function submit(event) {
    event.preventDefault();
    if (lock.current || step === "complete" || !event.currentTarget.reportValidity()) return;
    if (!firstName.trim()) {
      setError("Please enter your first name.");
      return;
    }
    if (genres.length < 1 || genres.length > 3) {
      setError("Choose 1–3 favorite genres to start. You can follow more in your Reader Portal.");
      return;
    }
    if (step === "verify" && !verificationCode.trim()) {
      setError("Please enter the verification code from your email.");
      return;
    }
    run(step === "signup" ? "Creating your account…" : step === "verify" ? "Verifying…" : "Continuing…", async () => {
      if (step === "resume") {
        remember(false);
        setStep("verify");
        setMessage("Enter the code from your email, or request a new one below.");
      } else if (step === "signup") {
        const result = await signUp({
          username: email.trim(), password,
          options: { userAttributes: {
            email: email.trim(), name: `${firstName.trim()} ${lastName.trim()}`.trim(),
          } },
        });
        const confirmed = result.isSignUpComplete === true;
        remember(confirmed);
        setStep(confirmed ? "setup" : "verify");
        setMessage(confirmed ? "Your account is ready. Continue setup below." : "Check your email for your verification code. You’re one step closer to your reader home.");
      } else if (step === "verify") {
        await confirmSignUp({ username: email.trim(), confirmationCode: verificationCode.trim() });
        remember(true);
        setStep("setup");
        await finishSetup();
      } else {
        await finishSetup();
      }
    });
  }

  function switchStep(next) {
    setError("");
    setMessage("");
    setVerificationCode("");
    setStep(next);
  }

  const signup = step === "signup";
  const editingDetails = signup || step === "resume";
  return (
    <div className="min-h-screen bg-bookish-cream text-bookish-royal">
      <PublicHeader audience="reader" />
      <main className="account-page flex flex-col items-center">
        <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-[0_12px_40px_rgba(63,37,107,0.10)] max-w-lg w-full border border-bookish-purple/8">
          <p className="text-sm text-center uppercase tracking-widest mb-3">Your next chapter starts here</p>
          <h1 ref={heading} tabIndex={-1} className="text-3xl text-center font-serif font-bold mb-4 text-bookish-royal">
            {step === "complete" ? "Welcome to the Circle" : step === "verify" ? "Check your inbox" : step === "setup" ? "Let’s open your reader home" : step === "resume" ? "Pick up where you left off" : "Create your free reader account"}
          </h1>
          {signup && <>
            <p className="text-center text-sm mb-4">Make a little room for your next favorite story. Save books to your to-be-read list, follow authors, and find your reading circles.</p>
            <p className="text-center text-sm mb-5">Your Circle Reader account is free. You’ll also get to participate in the <Link to="/reader-appreciation" className="underline">Reader Appreciation program</Link>, with eligible discounts from participating authors.</p>
          </>}
          {step === "verify" && <p className="text-sm mb-4 break-words">Enter the verification code sent to <strong>{email.trim()}</strong>. Check your spam folder if it hasn’t arrived.</p>}
          {step === "setup" && <p className="text-sm mb-4 break-words">Continue with <strong>{email.trim()}</strong> to finish account setup. You won’t need to enter another code if your email is already verified.</p>}
          {step === "complete" ? <p role="status" className="text-center">{returnTo && returnTo !== '/readerportal/mainpage' ? 'Returning to your page…' : 'Opening your Reader Portal…'}</p> : <form onSubmit={submit} aria-label="Reader signup" aria-busy={Boolean(busy)}>
            <fieldset disabled={Boolean(busy)} className="space-y-4 min-w-0">
              {editingDetails && <>
                <div><label className="account-field" htmlFor="signupreader-firstName">First name</label><input id="signupreader-firstName" autoComplete="given-name" required value={firstName} onChange={e => setFirstName(e.target.value)} className={inputClass} /></div>
                <div><label className="account-field" htmlFor="signupreader-lastName">Last name (optional)</label><input id="signupreader-lastName" autoComplete="family-name" value={lastName} onChange={e => setLastName(e.target.value)} className={inputClass} /></div>
              </>}
              {(signup || step === "resume") && <div><label className="account-field" htmlFor="signupreader-email">Email</label><input id="signupreader-email" autoComplete="email" type="email" required value={email} onChange={e => setEmail(e.target.value.trim())} className={inputClass} /></div>}
              <div>
                <label className="account-field" htmlFor="signupreader-password">Password</label>
                <p id="password-help" className="text-sm mb-2">{signup ? "Use a unique password with uppercase and lowercase letters, a number, and a symbol." : "Enter the password you chose when you created this account."}</p>
                <div className="relative">
                  <input id="signupreader-password" autoComplete={signup ? "new-password" : "current-password"} type={showPassword ? "text" : "password"} required aria-describedby="password-help" value={password} onChange={e => setPassword(e.target.value)} className={`${inputClass} pr-14`} />
                  <button type="button" aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword} onClick={() => setShowPassword(!showPassword)} className="absolute right-0 top-1/2 -translate-y-1/2 text-bookish-royal"><span aria-hidden="true">{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</span></button>
                </div>
              </div>
              {editingDetails && <fieldset aria-describedby="reader-genres-help reader-genres-count" className="min-w-0">
                <legend className="account-field">Choose 1–3 favorite genres</legend>
                <p id="reader-genres-help" className="text-sm mb-3">These are just to get you started. Your selections also subscribe you to Lady Liza’s monthly newsletters for those genres. You can follow more genres inside your Reader Portal, unsubscribe from genre emails, and explore every genre in the marketplace.</p>
                <p id="reader-genres-count" role="status" className="text-sm mb-3">{genres.length} of 3 selected.{genres.length === 3 && " Your starting circles are full. Unselect one to swap it, or follow more after signup."}</p>
                {genreStatus === "loading" && <p role="status" className="text-sm">Gathering your genre circles…</p>}
                {genreStatus === "error" && <div role="alert" className="text-sm mb-3"><p>We couldn’t load the genre choices. Please try again.{genres.length > 0 && " Your saved selections are still here."}</p><button type="button" className="underline min-h-[44px]" onClick={() => setGenreRetry(value => value + 1)}>Retry loading genres</button></div>}
                <div className="reader-signup-genres">
                  {normalizeGenres([...availableGenres, ...genres]).map(genre => {
                    const selected = genres.some(value => value.toLowerCase() === genre.toLowerCase());
                    const lines = genreLines(genre);
                    const longest = Math.max(...lines.map(line => line.length));
                    return <button key={genre} type="button" className="reader-signup-genre" aria-label={displayLabel(genre)} aria-pressed={selected} disabled={!selected && genres.length >= 3}
                      style={{ "--genre-color": genreColor(genre), "--genre-label-size": `${Math.min(12, 170 / longest)}px` }}
                      onClick={() => setGenres(current => selected ? current.filter(value => value.toLowerCase() !== genre.toLowerCase()) : current.length < 3 ? [...current, genre] : current)}>
                      <span className="reader-signup-genre-label">{lines.map((line, i) => <span key={i}>{line}</span>)}</span>
                      {selected && <span className="reader-signup-genre-check" aria-hidden="true"><Check size={16} /></span>}
                    </button>;
                  })}
                </div>
              </fieldset>}
              {step === "verify" && <div><label className="account-field" htmlFor="signupreader-verificationCode">Verification code</label><input id="signupreader-verificationCode" autoComplete="one-time-code" inputMode="numeric" required value={verificationCode} onChange={e => setVerificationCode(e.target.value)} className={inputClass} /></div>}
              <div role="alert" className="text-sm text-bookish-royal">{error}</div>
              <p role="status" aria-live="polite" className="text-sm">{busy || message}</p>
              <button type="submit" className={buttonClass} disabled={Boolean(busy)}>{busy || (signup ? "Create free reader account" : step === "verify" ? "Verify email and continue" : step === "resume" ? "Continue verification" : "Retry / continue setup")}</button>
              {step === "verify" && <>
                <button type="button" className="w-full min-h-[44px] underline" onClick={() => run("Sending a new code…", async () => { await resendSignUpCode({ username: email.trim() }); setMessage("A new code is on its way. Use the most recent email to verify your account."); })}>Resend code</button>
                <button type="button" className="w-full min-h-[44px] underline" onClick={() => { remember(true); switchStep("setup"); }}>Already verified? Continue setup</button>
              </>}
              {step === "setup" && <button type="button" className="w-full min-h-[44px] underline" onClick={() => { remember(false); switchStep("verify"); }}>Enter a verification code</button>}
              <button type="button" className="w-full min-h-[44px] underline" onClick={() => switchStep(signup ? "resume" : step === "resume" ? "signup" : "resume")}>{signup ? "Finish verifying an existing account" : step === "resume" ? "Create a new account instead" : "Use a different email"}</button>
            </fieldset>
          </form>}
          <p className="text-sm text-center mt-5">Already have an account? <Link to={loginPath} className="underline">Log in</Link></p>
          <Link to="/readerlanding" className="block text-center py-3 underline">Back to For Readers</Link>
          <p className="text-sm mt-4">Read our <Link className="underline" to="/privacy">Privacy Policy</Link> and <Link className="underline" to="/terms">Terms</Link>. Questions? <a className="underline" href="mailto:author@hayleyraejohnson.com">Contact Lady Liza</a>.</p>
        </div>
      </main>
    </div>
  );
}
